/**
 * Identidad del artefacto en el navegador.
 *
 * Los tres valores los inyecta el empaquetador al construir, y viajan dentro
 * del paquete hasta producción sin volver a tocarse. Eso es correcto: la
 * versión y el commit son propiedades del artefacto.
 *
 * El entorno NO está aquí. Es lo contrario: se lee al arrancar desde
 * `entorno.json`, porque el mismo paquete tiene que servir a dev, staging y
 * producción. Si el entorno se horneara, habría que reconstruir por destino y
 * lo que se probó en staging dejaría de ser bit a bit lo que entra a
 * producción.
 */

declare const __APP_VERSION__: string | undefined;
declare const __APP_COMMIT__: string | undefined;
declare const __APP_BUILT_AT__: string | undefined;

export interface BuildInfo {
  /** Versión semántica del último lanzamiento. Ej. `1.4.2`. */
  readonly version: string;
  /** SHA corto del commit que produjo el artefacto. Ej. `a3f9c1d`. */
  readonly commit: string;
  /** Instante de construcción en ISO 8601 con zona. */
  readonly builtAt: string;
}

const SIN_DATO = 'desconocido';

/**
 * En desarrollo el empaquetador no inyecta nada y las tres constantes no
 * existen. Se comprueba con `typeof` y no con una comparación: leer una
 * constante no declarada lanza, y un error de arranque por consultar la
 * versión sería absurdo.
 */
export function leerBuildInfo(): BuildInfo {
  return {
    version: typeof __APP_VERSION__ === 'string' ? __APP_VERSION__ : SIN_DATO,
    commit: typeof __APP_COMMIT__ === 'string' ? __APP_COMMIT__ : SIN_DATO,
    builtAt: typeof __APP_BUILT_AT__ === 'string' ? __APP_BUILT_AT__ : SIN_DATO,
  };
}

/**
 * Forma corta para el pie de la pantalla de acceso y para soporte:
 * `1.4.2 · a3f9c1d`.
 *
 * Lleva el commit además de la versión porque entre dos lanzamientos hay
 * muchos artefactos con la misma versión, y la pregunta que se hace de verdad
 * —«¿está desplegado mi cambio?»— solo la responde el commit.
 */
export function formatearBuildInfo(info: BuildInfo): string {
  return `${info.version} · ${info.commit}`;
}
